/**
 * **组接口的声明与镜像** —— 从 `node/group.ts` 向下拆出来的前半部分。
 *
 * ## 为什么按这里切
 *
 * `group.ts` 有 31 个声明，量下来是**两件事**：
 *
 * ```text
 *   本文件（接口）：变参行怎么借、外层静态行怎么镜像、
 *                 进出的接口刷新        ← 15 个声明，583 行
 *   group.ts（规划）：建组 / 解组         ← 10 个声明，339 行
 * ```
 *
 * 两者的依赖是**单向**的，而且很窄：
 *
 * ```text
 *   规划 → 接口： 只用 groupInputRows / groupOutputRows（2 个）
 *   接口 → 规划： 一个都不用
 * ```
 *
 * 所以按**向下**拆：规划是上层（它调接口），接口是下层。
 * `group.ts` 继续 re-export 本文件的名字，既有引用一行都不用改。
 *
 * ## 这一半在讲什么（读代码前先看这个）
 *
 * 组的接口是**两侧对称的静态镜像**：
 *
 * ```text
 *   组的参数（里面 __group_in）           组的结果（里面 __group_out）
 *   ─────────────────────────           ──────────────────────────
 *   值流**出去** → 数 source 侧           值流**进来** → 数 target 侧
 *   变参行（`freeSlot` 留一个接线空槽）     变参行（同上）
 *   外面镜像成 in.0 / in.1 …              外面镜像成 out0 / out1 …
 * ```
 *
 * ⚠️ 三条最容易搞混的规矩（都在这儿）：
 *   1. **数量由里面决定**（外面连不连都改不了结构），外面只是镜像；
 *   2. **`freeSlot` 那个空槽是接线入口**，不是「声明了几路」；
 *   3. **两个接口节点方向相反**（一个 source 侧、一个 target 侧）。
 */
import type {
  GraphNodeModel,
  InputRow,
  NodeRow,
  OutputRow,
  RepeaterRow,
  SubGraph,
} from '../types/node'
import type { SocketType } from '../types/socket'
import { repeaterCount } from './rows'

export const GROUP_INPUT_NODE = '__group_in'
export const GROUP_OUTPUT_NODE = '__group_out'

/** 组节点上「输入」那一行的变参行 id */
export const GROUP_IN_REPEATER = 'in'
/** 输出节点上「结果」那一行的变参行 id */
export const GROUP_OUT_REPEATER = 'out'

/** 组的外部端口描述（建组时从跨边界的边推出来） */

/**
 * 组的 rows。
 *
 * 输入是一个**变参行**（数量由外部加）；输出是若干显式行
 * （数量由内部接了几路决定，见 `groupRowsFromGraph`）。
 */
export function groupRowsFor(
  inputCount: number,
  outputCount: number,
  /**
   * 每一路结果**在内部确认出来的类型**。
   *
   * 里面接的是 `text`，外面这一路就该是 `text` —— 不传的话
   * 组对外永远是 `any`，外面接错也发现不了。
   */
  outputTypes?: readonly (SocketType | undefined)[],
  /**
   * 每一路参数**的类型**（组内部声明的，或外面确认过的那份）。
   *
   * ⚠️ 类型和**数量**的流向是分开的：
   *   · 数量 —— 里面说了算（外面接线改不了结构）；
   *   · 类型 —— 外面连了什么就确认成什么（类型不是结构）。
   */
  inputTypes?: readonly (SocketType | undefined)[],
  /**
   * 固定参数（`param.{k}`）—— **排在变参之前**（见 `GroupInterfaceDecl`）。
   * 不传就是「只有一条变参行」那种常见情况。
   */
  fixed?: readonly { id: string; label?: string; type?: SocketType }[],
  /**
   * ★ 每一路参数**用户起的名字**（内层接口行上的 `names`）。
   *
   * ⚠️ 没有它的话，用户在里面把「参数 1」改成「纸张宽度」，
   * **外面看不到** —— 而外层端口名正是他接线时唯一能看到的提示。
   * 改名的意义就是「让外面知道这一路是什么」，不同步等于改名没用。
   */
  inputNames?: readonly (string | undefined)[],
  /** ★ 每一路结果用户起的名字（内层 `results` 行上的 `names`） */
  outputNames?: readonly (string | undefined)[],
): NodeRow[] {
  /*
   * ★ 外层输入端口是**静态行**，不是变参行。
   *
   * 为什么：变参行自带「末尾留一个空槽」，于是**外面连一根线就能给组
   * 加一路参数** —— 那等于让外面改掉组的内部结构（用户的原话：
   * 「防止因为外部改变组内部的结构」）。静态行没有空槽，
   * 想加一路就得进组、在内部接口节点上点「＋」。
   *
   * ⚠️ handle 不变：id 仍然是 `in.0` / `in.1`（静态行直接拿这个当 id），
   * 所以已有文档和边不用迁移。
   */
  const fixedInputs: InputRow[] = (fixed ?? []).map((param): InputRow => ({
    kind: 'input',
    id: param.id,
    label: param.label ?? param.id,
    socket: { id: param.id, type: param.type ?? 'any' },
    /* 接口行不带内联控件：组参数的值只能从外面接进来 */
  }))

  /*
   * ★ 变参那几路在外面是**静态行**（一进一出各一根线）。
   *
   * 这里曾经有一个 `interfaceDisplay: 'variadic'` 的分支：把这几路显成
   * **一条集合端口**，外面一根集合边整条喂进来。**已删** ——
   * 集合这个类型本身删掉了（见 docs/collection-ports.md 的现状一节），
   * 而「外面连一根线就多一路参数」那条老纪律仍然靠静态行守着。
   */
  const variadicInputs: InputRow[] = Array.from(
    { length: Math.max(0, inputCount) },
    (_, index): InputRow => {
      const id = `${GROUP_IN_REPEATER}.${index}`
      return {
        kind: 'input',
        id,
        /* 用户起的名字优先；没有才回退「参数 N」 */
        label: inputNames?.[index] || `参数 ${index + 1}`,
        socket: { id, type: inputTypes?.[index] ?? 'any' },
        /* 接口行**不带内联控件**：组参数的值只能从外面接进来 */
      }
    },
  )

  /* ★ 固定在前、变参在后 —— 这一条顺序规则两边共用 */
  const inputs: InputRow[] = [...fixedInputs, ...variadicInputs]

  // 输出同理：里面一路都没接，外面就不该有结果端口
  const outputs: OutputRow[] = Array.from(
    { length: Math.max(0, outputCount) },
    (_, index) => ({
      kind: 'output',
      id: `out${index}`,
      label: `结果 ${index + 1}`,
      socket: { id: `out${index}`, type: outputTypes?.[index] ?? 'any' },
    }),
  )

  return [...inputs, ...outputs]
}

/** 组当前声明了几个输入（读变参行的实例数） */
/**
 * **内部接口声明了几路输入** —— 接口的**基准**数量。
 *
 * ⚠️ 这是「内部决定外部」那一侧（和输出对称）。踩过的教训：
 * 进组时原来用的是「外面连了几路」（`connectedRepeaterCount`），
 * 于是一个**自带接口的资产**在外部还没接线时，进门一看内部接口行
 * **被清空了** —— 里面接好的线全部指向不存在的端口，
 * Vue Flow 直接报 `EDGE_SOURCE_TARGET_MISSING`。
 *
 * 现在的规矩：**内部声明是基准，外部连线只能往上加，不会往下削**。
 * （接口只增不减 —— 要缩得显式删参数，那个 UI 还没做。）
 */
/**
 * 组接口的**声明**（源头在组内部）。
 *
 * ★ 一个组的参数可以**混着来**（用户要的「组自己定义哪些是固定的、哪些是活的」）：
 *   · `fixed`    —— 固定参数，逐个声明（内部是输出行，id 形如 `param.0`）；
 *   · `variadic` —— 变参参数，一条变参行（内部是输出方向的变参行 `in`），
 *                   用户在组里点「＋ / ✕」增删。
 *
 * ⚠️ **顺序规则：固定参数排在变参之前。** 外层镜像、求值灌值都按这个顺序，
 * 规则只此一条（外层看起来的顺序 = 组内部承诺的顺序）。
 *
 * ⚠️ 老文档里的内部接口是 N 条静态输出行、id 形如 `in.{i}` ——
 * 那时它们**全都是变参**（「外面连了几路就是几路」的年代）。
 * 所以判据是：`in.` 前缀的输出行 = 老形状的变参；别的输出行 = 固定参数。
 */
export interface GroupInterfaceDecl {
  fixed: { id: string; label?: string; type?: SocketType }[]
  variadic: number
  variadicTypes?: readonly (SocketType | undefined)[]
  /**
   * 内部**有没有**那条变参行。
   *
   * ⚠️ 这和 `variadic === 0` **不是一回事**，混起来会出 bug：
   *   · 有变参行、但声明 0 路 —— 合法的「现在没有活的参数」（以后点＋加）；
   *   · 没有变参行 —— 老文档（内部是 `in.{i}` 静态行）才这样。
   * 上一轮就是没区分，于是「只有固定参数」的组每次进出都凭空长一路变参。
   */
  hasVariadicRow: boolean
}

/**
 * 从内部图读出接口声明。
 *
 * ★★ **变参那几路：件数从「里面接了几路」推**（和输出结果那一侧同一条规矩）。
 *
 * ```
 *   组的参数（里面）                      组的结果（里面）
 *   ─────────────────                    ─────────────────
 *   从 __group_in 出发的边数   ←  对称 →   指向 __group_out 的边数
 *   （`lastConnectedFromEdges`）           （`lastConnectedFromEdges`）
 * ```
 *
 * 于是**接一根线就长一路参数** —— 和变参行本来的语义（「连一个空就多一个空」）
 * 一致，不需要任何「＋」按钮之类的**外物**。用户要的就是这个：
 * 「我想要输出结果那种变参行自动增加参数的功能，和输入变参行的语义差不多」。
 *
 * ⚠️ 不能读 `repeaterCount(row)` —— 那是**渲染**件数，看的是 `row.connected`
 * 这个**视图状态**，而子图里的行**没有**那份状态（它由画布层的投影同步）。
 * 读它的话结果恒等于 `min`，表现就是「参数数量固定在建组那一刻」。
 * （实测：里面接了 3 路，`repeaterCount` 给 3 是因为 `min` 恰好是 3；
 *   `connectedRepeaterCount` 给 **0** —— 视图状态在子图里是空的。）
 */
export function groupInterfaceOf(graph: SubGraph): GroupInterfaceDecl {
  const node = graph.nodes.find((n) => n.id === GROUP_INPUT_NODE)
  const decl: GroupInterfaceDecl = { fixed: [], variadic: 0, hasVariadicRow: false }
  if (!node) return decl

  const prefix = `${GROUP_IN_REPEATER}.`
  let legacyVariadic = 0
  for (const row of node.rows) {
    if (row.kind === 'repeater' && row.id === GROUP_IN_REPEATER) {
      /*
       * ★★ 参数路数从**边**推，取两侧的**较大者**：
       *
       *   ① **源侧** —— `__group_in.in.{i} → X`：里面接了几路参数
       *      （用户在里面接线就长一路，这是「加参数」的正常入口）；
       *   ② **目标侧** —— `X → __group_in.{i}`：老形状（静态输出行）的
       *      内部布线，也就是**资产**那种「内部写死了参数怎么用」的组。
       *
       * ⚠️ 只数①会把**资产**的接口读成 0：`asset.format-date` 的 `in.0`
       * 向内连了 3 个节点（源侧 3 条），但它**没有被外部喂过**
       * —— 用户报过的那个 bug 就是「放下来进组一看，内部接口被削掉了」。
       * 取较大者既保住「外部没接线时接口不被削掉」，又保住
       * 「里面接一根长一路」。
       *
       * ⚠️ **不吃 `row.min`** —— 那个数是**渲染**用的（末尾那个空槽是接线入口，
       * 见 `freeSlot`），不是「声明的路数」。吃了它的后果是
       * 「里面渲染 1 个空槽 → 外面就多出一个连不上的端口」。
       * 这个仓库为「把渲染数当接口数」已经踩过三次，这是第三次。
       */
      const outbound = lastConnectedFromEdges(graph.edges, GROUP_INPUT_NODE, row, 'source')
      const inbound = lastConnectedFromEdges(graph.edges, GROUP_INPUT_NODE, row, 'target')
      /*
       * ⚠️ **没有边就是「还没有声明」**（留给老形状那条兜底），
       * 不能写成 `最末序号 + 1` —— 那会把「一条边都没有」误算成 1 路。
       */
      const wired = outbound < 0 && inbound < 0 ? 0 : Math.max(outbound, inbound) + 1
      decl.variadic = wired
      decl.variadicTypes = row.instanceTypes
      decl.hasVariadicRow = true
      continue
    }
    if (row.kind !== 'output') continue
    if (row.id.startsWith(prefix)) {
      /* 老文档：这些行就是那时的「变参」 */
      legacyVariadic += 1
      continue
    }
    decl.fixed.push({ id: row.id, label: row.label, type: row.socket.type })
  }
  /*
   * ★ **老文档的兜底**：内部是 `in.{i}` 静态行（**没有**变参行）时，
   * 那些行就是当年的「变参声明」—— 一份老文档外面有几个参数端口，
   * 进来时就得有几个，不能因为「没有边」而读成 0（那会把老文档的接口清空）。
   *
   * ⚠️ 只在**一根线都没接**时才用这个数：一旦里面接了线，
   * 「接了几路」就是真相（那时老行已经归一化成变参行、边是新的声明）。
   */
  if (decl.variadic === 0 && legacyVariadic > 0) decl.variadic = legacyVariadic
  return decl
}

/**
 * ★ 给组的内部接口节点**加一个固定参数**，返回新行的 id。
 *
 * 固定参数 vs 变参（用户要的「组自己定义哪些是固定的、哪些是活的」）：
 *   · **固定** —— 逐个声明，id `param.{k}`，外面看到的就是这一路；
 *   · **变参** —— 那一条变参行（`in`），点「＋ / ✕」增删。
 *
 * ⚠️ 插在**变参行之前**：「固定在前」是两边共用的顺序规则
 * （见 `GroupInterfaceDecl`），外面镜像按它排。
 *
 * ⚠️ 编号取**最小的没用过的号** —— 也就是「按位置寻址」，
 * 和变参实例（`in.0` / `in.1`）是同一套约定，不另立规矩。
 * 删掉 `param.0` 再加一个，拿到的还是 `param.0`：
 * 外面那条线接的是**这个位置**，位置还在，线就还在。
 *
 * （写第一版时我在注释里写了「编号不复用」，测试当场把这个矛盾顶出来：
 * 代码是「最小可用号」。想清楚之后决定**跟变参一致**，改注释不改代码 ——
 * 同一套东西里两种编号策略比复用更难解释。）
 *
 * ⚠️ 纯函数（只改传进来的 model）—— 放在节点层是为了能被直接断言：
 * 这条规则原来写在 composable 里，只有把图跑起来才测得到。
 */
export function addFixedParamRow(model: GraphNodeModel): string | null {
  if (model.type !== 'group.input') return null
  const used = new Set(model.rows.filter((r) => r.kind === 'output').map((r) => r.id))
  let k = 0
  while (used.has(`param.${k}`)) k += 1
  const id = `param.${k}`
  const row: OutputRow = {
    kind: 'output',
    id,
    label: `固定参数 ${k + 1}`,
    socket: { id, type: 'any' },
  }
  const at = model.rows.findIndex((r) => r.kind === 'repeater' && r.id === GROUP_IN_REPEATER)
  model.rows =
    at < 0 ? [...model.rows, row] : [...model.rows.slice(0, at), row, ...model.rows.slice(at)]
  return id
}

/** 没有固定参数时的简写（常见路径与测试用） */
export function variadicInterface(
  count: number,
  types?: readonly (SocketType | undefined)[],
): GroupInterfaceDecl {
  return { fixed: [], variadic: count, variadicTypes: types, hasVariadicRow: true }
}

/** 接口**总有几路**参数（固定 + 变参） */
export function innerInterfaceCount(graph: SubGraph): number {
  const decl = groupInterfaceOf(graph)
  return decl.fixed.length + decl.variadic
}

/**
 * 组对外**有几路输入** —— 数那些静态参数行。
 *
 * ⚠️ 老文档里这一侧是**变参行**（`in`），所以两种形状都要认：
 * 认不出来就会把「有 3 路输入的组」读成 0 路。
 */
export function groupInputCount(rows: readonly NodeRow[]): number {
  const staticRows = groupParamRows(rows)
  if (staticRows.length > 0) return staticRows.length
  const row = rows.find((r) => r.kind === 'repeater' && r.id === GROUP_IN_REPEATER)
  if (row?.kind !== 'repeater') return 0
  return repeaterCount(row)
}

/**
 * 组外面的**参数端口行**（静态输入行，id 形如 `in.0`）。
 *
 * ⚠️ 「哪些行是参数」这条规则只在这里写一次：求值器按它把外面的值
 * 灌给组内部，`assetRows` 按它套端口名 —— 两处各写一遍会不一致，
 * 而症状是「端口名对不上求值读的那一路」。
 */
export function groupParamRows(rows: readonly NodeRow[]): InputRow[] {
  /*
   * ★ 组的输入行**全都是参数**（固定参数 `param.{k}` + 变参那几路 `in.{i}`）
   * —— 组节点上除了参数就只剩结果行，没有别的输入。
   *
   * ⚠️ 原来靠 `in.` 前缀认，加入固定参数后就不够了。改成「全部输入行」
   * 反而更准确，也让求值器不必知道 id 的命名规则。
   */
  return rows.filter((r): r is InputRow => r.kind === 'input')
}

/** 组当前声明了几路输出 */
export function groupOutputCount(rows: readonly NodeRow[]): number {
  return rows.filter((r) => r.kind === 'output').length
}

/**
 * 内部**输入节点**的 rows。
 *
 * 把外部的输入变参行**逐个展开成输出行** —— 数量完全由外部决定，
 * 里面只是把它显示成一排可以接线的输出端口。
 *
 * 行 id 用 `in.{i}`，跟外部变参行的 handle 一一对应。
 */
export function groupInputRows(
  /**
   * 组**声明**了几路参数（接口的基准数量）。可以是 **0**。
   *
   * ⚠️ 参数的真相在**里面**（用户在里面点「＋」加一路），外面只是镜像 ——
   * 见 `subgraph.ts` 里 `refreshGroupInterface` 的注释。
   */
  count: number,
  /**
   * 每一路**确认出来的类型**。
   *
   * 外部没连的那一路是 `any`（通配），连上了就是上游那个类型 ——
   * 把这份类型一起传进来，组内的连线校验才不是摆设。
   */
  types?: readonly (SocketType | undefined)[],
): NodeRow[] {
  /*
   * ★ 一条**输出方向**的变参行，而不是 N 条静态输出行。
   *
   * 为什么要变参行：接口的「几路」是**可增删**的，而变参行本来就带
   * 那套机制（实例自己的名字 `names`、类型 `instanceTypes`、连接态
   * `connected`、以及「＋ / ✕」的增删）。静态行要做到这些只能整份重写 ——
   * 昨天那个「一进组里面的线全断了」就是重写时把行 id 弄丢造成的。
   *
   * ⚠️ handle **不变**：`id` 还是 `in`、默认模板 `${id}.{i}`
   * → 实例句柄还是 `in.0` / `in.1`（和静态行的 id 完全一样），
   * 所以已有文档里那些指向 `in.0` 的边一条都不用改。
   *
   * ★★ `min` = **声明路数 + 1** —— **末尾那个空槽就是「加参数」的入口**。
   *
   * 用户要的语义：「接一根线就长一路参数，不要按钮」。
   * 那就必须**永远留一个可接的空位**，否则是个死锁：
   *
   * ```text
   *   里面 0 个输出端口 → 没地方接线 → 边数 0 → 永远 0 个端口 → …
   * ```
   *
   * ⚠️ 这一条是**实测**出来的：第一版写成 `min = count`，
   * 进去看到的输入节点**一个输出端口都没有**（用户报的正是这个）。
   * 而**输出结果那一侧一直能用**，正是因为它的 `min` 恒为 1
   * （`groupOutputRows`）—— 那里天然留了一个空槽。两侧现在对称了。
   *
   * ⚠️ **这个 `min` 不是接口数量**：外层的端口数由 `groupInterfaceOf`
   * **从边推**，它**不吃 `min`**。混淆这两者会让外面凭空多一个端口
   * （「进出组多参数」那个老 bug 的同一个来源）。
   */
  return [
    {
      kind: 'repeater',
      id: GROUP_IN_REPEATER,
      label: '参数',
      socketType: 'any',
      side: 'output',
      min: Math.max(0, count),
      max: 32,
      /*
       * ★★ **末尾留一个空槽** —— 那就是「加参数」的入口（用户要的
       * 「接一根线长一路」，不要按钮）。
       *
       * 没有它就是个死锁：
       * ```text
       *   里面 0 个输出端口 → 没地方接线 → 边数 0 → 永远 0 个端口 → …
       * ```
       * 实测：第一版没加时，进去看到的输入节点**一个输出端口都没有**。
       *
       * ⚠️ 它是**渲染**用的那个槽，**不是**接口数量 ——
       * 外层端口数由 `groupInterfaceOf` 从**边**推，不看 `min` 也不看这个。
       */
      freeSlot: true,
      instanceTypes: types ? [...types] : undefined,
    },
  ]
}

/**
 * 内部**输出节点**的 rows。
 *
 * 一个输入变参行 —— 里面接几路，外面就显示几个结果端口。
 */
/**
 * 内部**输出节点**的 rows：一个输入变参行。
 *
 * ⚠️ `min` **恒为 1**，不跟着「当前有几路结果」走。
 *
 * 之前把建组时的输出数烘进 `min`，于是删光连线之后里面**还留着两行** ——
 * `min` 是「至少渲染几行」，而结果的数量应该完全由「接了几路」决定。
 * 烘死的话它就再也缩不回去了。
 */
export function groupOutputRows(): NodeRow[] {
  return [
    {
      kind: 'repeater',
      id: GROUP_OUT_REPEATER,
      label: '结果',
      socketType: 'any',
      min: 1,
      max: 32,
      /* 同上：里面也要有个空槽才能「接一路结果」 */
      freeSlot: true,
    },
  ]
}

/**
 * 由边推导某一行「最后一个已连接的实例序号」。
 *
 * 子图里的行没有 `connected` 视图状态（那是在画布层同步的），
 * 所以这里直接从边算。
 */
export function lastConnectedFromEdges(
  edges: readonly {
    source?: string
    sourceHandle?: string | null
    target?: string
    targetHandle?: string | null
  }[],
  nodeId: string,
  row: RepeaterRow,
  /**
   * 从这个节点的**哪一侧**找边。
   *
   * ⚠️ **两个接口节点的方向是相反的**，这一点漏了就会静默算出「一路都没有」：
   *   · `__group_out`（结果）—— 值**流进**来，边是 `X → __group_out.out.{i}` → 看 **target** 侧
   *   · `__group_in`（参数）—— 值**流出去**，边是 `__group_in.in.{i} → Y` → 看 **source** 侧
   *
   * 第一版只写了 target 那一侧，于是「里面接了 3 路参数」被读成 0 路，
   * 外层一个端口都不出（而结果那一侧一直是对的，所以看不出问题）。
   */
  side: 'source' | 'target' = 'target',
): number {
  const pattern = row.handlePattern ?? `${row.id}.{i}`
  let last = -1
  for (const edge of edges) {
    if ((side === 'target' ? edge.target : edge.source) !== nodeId) continue
    const handle = (side === 'target' ? edge.targetHandle : edge.sourceHandle) ?? ''
    for (let i = 0; i < 64; i += 1) {
      if (pattern.replace('{i}', String(i)) === handle) {
        if (i > last) last = i
        break
      }
    }
  }
  return last
}

/**
 * 从子图里那两个接口节点**反推**组的外部 rows。
 *
 * ⚠️ 只反推**输出**那一侧 —— 输入是外部决定的，反推会把它改掉。
 * 所以 `inputCount` 由调用方（外部的现状）传进来。
 */
export function groupRowsFromGraph(
  graph: SubGraph,
  /**
   * 对外的输入路数。
   *
   * ⚠️ **默认取内部声明的那个数** —— 别让调用方随手传一个「外面连了几路」，
   * 那正是把内部接口削掉的来源。要加就传一个**更大的**数（外部连了新参数）。
   */
  inputCount: number = innerInterfaceCount(graph),
  outputTypes?: readonly (SocketType | undefined)[],
  inputTypes?: readonly (SocketType | undefined)[],
): NodeRow[] {
  const outputNode = graph.nodes.find((n) => n.id === GROUP_OUTPUT_NODE)
  const row = outputNode?.rows.find(
    (r): r is RepeaterRow => r.kind === 'repeater' && r.id === GROUP_OUT_REPEATER,
  )

  /*
   * 用 `last + 1` 而不是 `repeaterCountFor`：后者会多留一个空槽
   * （渲染上要留一行好继续连），但外部端口该镜像的是**实际接了几路**。
   */
  // 里面一路都没接 → 0 路输出（不是 1）
  const wired = row
    ? lastConnectedFromEdges(graph.edges, GROUP_OUTPUT_NODE, row) + 1
    : 0
  /*
   * ★ 接口的**数量与形状**都从内部图的声明来（`groupInterfaceOf`）：
   * 固定参数逐个镜像、变参那几路按实例数镜像，顺序固定在前。
   *
   * ⚠️ 回退到调用方传来的 `inputCount` **只在「内部根本没有变参行」时**
   * （老文档：内部是 `in.{i}` 静态行）。有一条声明 0 路的变参行
   * **不算**没有 —— 那是「现在没有活的参数」，不能再给它补出几路来。
   * （上一轮写成 `decl.variadic || inputCount`，于是「只有固定参数」的组
   * 每次进出都凭空长一路变参 —— 用户报的正是这个。）
   */
  const decl = groupInterfaceOf(graph)
  /*
   * ★ 用户给每一路起的名字（内层接口行上的 `names`）—— 要镜像到外面。
   * 不传的话，里面把「参数 1」改成「纸张宽度」，外面还是「参数 1」。
   */
  const inRow = graph.nodes
    .find((n) => n.id === GROUP_INPUT_NODE)
    ?.rows.find((r): r is RepeaterRow => r.kind === 'repeater' && r.id === GROUP_IN_REPEATER)
  return groupRowsFor(
    decl.hasVariadicRow ? decl.variadic : inputCount,
    wired,
    row?.instanceTypes ?? outputTypes,
    inputTypes,
    decl.fixed,
    inRow?.names,
    row?.names,
  )
}

/** 组从菜单拖出来时，子图里该有的两个接口节点 */
export function emptyGroupGraph(): SubGraph {
  return {
    nodes: [
      {
        id: GROUP_INPUT_NODE,
        type: 'group.input',
        title: '输入',
        category: 'utility',
        // 刚拖出来时外面什么都没连 → 内部一行都没有
        rows: groupInputRows(0),
      },
      {
        id: GROUP_OUTPUT_NODE,
        type: 'group.output',
        title: '输出',
        category: 'utility',
        /*
         * 输出节点保留一个空槽（`min: 1`）—— 里面总得有个地方可以接。
         * 但**外部**的结果端口是按「实际接了几路」算的，所以外面仍然是 0 路。
         */
        rows: groupOutputRows(),
      },
    ],
    edges: [],
    // 左进右出，进去之后一眼看得懂
    positions: {
      [GROUP_INPUT_NODE]: { x: 0, y: 0 },
      [GROUP_OUTPUT_NODE]: { x: 460, y: 0 },
    },
  }
}

/**
 * 按外部的现状，把内部输入节点的行**对齐**过去。
 *
 * 进入子图、以及外部改了输入数量时要调一次。
 */
export function syncInputNode(
  graph: SubGraph,
  inputCount: number,
  types?: readonly (SocketType | undefined)[],
): void {
  const node = graph.nodes.find((n) => n.id === GROUP_INPUT_NODE)
  if (!node) return

  /*
   * ⚠️ **就着已有的那条变参行改，不要整份替换。**
   *
   * 替换会顺手丢掉用户给每一路起的名字（`names`）—— 名字是作者写的
   * 接口文档，而同步每次进组都会跑一遍，丢掉它的表现是
   * 「进去改个名字，出来再进去就没了」。数量、类型是可以覆盖的，
   * 名字不是。
   */
  const existing = node.rows.find(
    (r): r is RepeaterRow => r.kind === 'repeater' && r.id === GROUP_IN_REPEATER,
  )
  const fresh = groupInputRows(inputCount, types)[0]
  if (!existing || fresh?.kind !== 'repeater') {
    node.rows = groupInputRows(inputCount, types)
    return
  }
  existing.min = fresh.min
  existing.instanceTypes = fresh.instanceTypes
  /* 名字数组跟着实例数裁一下，留着多余的名字会在加回实例时「复活」 */
  if (existing.names && existing.names.length > inputCount) {
    existing.names = existing.names.slice(0, Math.max(0, inputCount))
  }
}

/* ══════════════════════════════════════════════════════════════
   从选中的节点建组
   ══════════════════════════════════════════════════════════════ */
